import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ChevronRight } from 'lucide-react';
import { collection, query, orderBy, onSnapshot, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BBALoader from '../../components/BBALoader';
import { computeHiringBalance } from '../../utils/hiringBalance';
import ContactWithLogo from '../../components/crm/ContactWithLogo';

// Pipeline stages for the Hiring Fulfillment card — the same four columns, in
// the same order, as the CRM hiring board (/crm/fulfillment/hr). "Available" is
// derived (clients with an unused Hiring deliverable); the other three bucket
// stored positionRequest statuses. Legacy pending/approved fold into In
// Progress, matching the board.
const STAGES = [
  {
    key: 'bid',
    label: 'Bid',
    emoji: '💲',
    ring: 'ring-pink-500',
    tile: 'bg-pink-50 dark:bg-pink-900/30',
    text: 'text-pink-600 dark:text-pink-400',
    dot: 'bg-pink-500',
    empty: 'No bids right now.',
  },
  {
    key: 'available',
    label: 'Available',
    emoji: '🎯',
    ring: 'ring-indigo-500',
    tile: 'bg-indigo-50 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
    dot: 'bg-indigo-500',
    empty: 'No clients have an unused hire right now.',
  },
  {
    key: 'in-progress',
    label: 'In Progress',
    emoji: '🚀',
    ring: 'ring-blue-500',
    tile: 'bg-blue-50 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    dot: 'bg-blue-500',
    empty: 'Nothing is being worked right now.',
  },
  {
    key: 'completed',
    label: 'Completed',
    emoji: '🏁',
    ring: 'ring-emerald-500',
    tile: 'bg-emerald-50 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-500',
    empty: 'No completed hires yet.',
  },
];

const STATUS_TO_STAGE = {
  bid: 'bid',
  pending: 'in-progress',
  approved: 'in-progress',
  'in-progress': 'in-progress',
  completed: 'completed',
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatShortDate = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
};

const contactName = (account) =>
  `${account?.contact?.firstName || ''} ${account?.contact?.lastName || ''}`.trim();

/**
 * Hiring Fulfillment — the HR team's card on the main CRM Dashboard. Sits in
 * the same row as the rotating gallery (where Owner / Executive users see the
 * Leads & Clients chart). Four stage tiles filter a visual grid of clients and
 * requests, each led by the main contact's photo and the company logo.
 */
const HRFulfillmentWidgets = ({ className = '', isMobile = false }) => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [balances, setBalances] = useState({});
  const [clientsLoading, setClientsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [stage, setStage] = useState('in-progress');

  // Client accounts + Hiring deliverable balances (drives "Available").
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.company)
          .sort((a, b) => String(a.company).localeCompare(String(b.company)));
        setClients(list);
        const computed = await Promise.all(list.map((account) => computeHiringBalance(account)));
        if (cancelled) return;
        const map = {};
        list.forEach((account, idx) => { map[account.id] = computed[idx]; });
        setBalances(map);
      } catch (err) {
        console.error('Error loading clients / hiring balances:', err);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live position requests.
  useEffect(() => {
    const q = query(collection(db, 'positionRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data(), status: d.data().status || 'in-progress' })));
        setRequestsLoading(false);
      },
      (err) => {
        console.error('Error fetching position requests:', err);
        setRequests([]);
        setRequestsLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const clientsById = useMemo(() => {
    const map = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);

  const clientsByCompany = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      if (c.company) map[String(c.company).toLowerCase().trim()] = c;
    });
    return map;
  }, [clients]);

  const resolveAccount = useCallback((request) => {
    if (request.accountId && clientsById[request.accountId]) return clientsById[request.accountId];
    const name = (request.companyName || request.company || '').toLowerCase().trim();
    return name ? clientsByCompany[name] || null : null;
  }, [clientsById, clientsByCompany]);

  // Bucket everything into the four stages as uniform tiles.
  const tilesByStage = useMemo(() => {
    const buckets = { bid: [], available: [], 'in-progress': [], completed: [] };

    clients.forEach((account) => {
      const balance = balances[account.id];
      if (!balance || balance.remaining <= 0) return;
      buckets.available.push({
        id: `client-${account.id}`,
        account,
        company: account.company,
        subtitle: contactName(account),
        meta: `${balance.remaining} hire${balance.remaining === 1 ? '' : 's'} available`,
        onClick: () => navigate('/crm/fulfillment/hr'),
      });
    });

    requests.forEach((request) => {
      const key = STATUS_TO_STAGE[request.status];
      if (!key) return;
      const account = resolveAccount(request);
      const company = account?.company || request.companyName || request.company || 'Unknown Company';
      const dateLabel = formatShortDate(request.completedAt || request.updatedAt || request.createdAt);
      buckets[key].push({
        id: request.id,
        account,
        company,
        subtitle: request.position || 'Position request',
        meta: dateLabel,
        onClick: () => navigate(`/crm/fulfillment/hr/request/${request.id}`),
      });
    });

    return buckets;
  }, [clients, balances, requests, resolveAccount, navigate]);

  const activeStage = STAGES.find((s) => s.key === stage) || STAGES[0];
  const tiles = tilesByStage[stage] || [];
  const loading = stage === 'available' ? clientsLoading : requestsLoading || clientsLoading;

  return (
    <div className={`bg-white dark:bg-gray-900 p-6 rounded-lg shadow-sm hover-lift flex flex-col ${className}`}>
      <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold text-center dark:text-white mb-5`}>
        Hiring Fulfillment
      </h2>

      {/* Stage tiles */}
      <div className={`grid gap-3 mb-5 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
        {STAGES.map((s) => {
          const count = clientsLoading && s.key === 'available' ? null : (tilesByStage[s.key] || []).length;
          const selected = s.key === stage;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setStage(s.key)}
              className={`group rounded-xl border text-left p-3 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                selected
                  ? `border-transparent ring-2 ${s.ring} ${s.tile} shadow-sm`
                  : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${s.tile}`}>
                  <span className="text-xl" aria-hidden="true">{s.emoji}</span>
                </div>
                <div className="min-w-0">
                  <p className={`text-2xl font-bold leading-none ${count === 0 ? 'text-gray-300 dark:text-gray-600' : s.text}`}>
                    {count === null ? '…' : count}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-gray-600 dark:text-gray-300 truncate">{s.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Tiles for the selected stage */}
      <div className="flex-1 min-h-[220px]">
        {loading ? (
          <div className="h-full flex items-center justify-center py-10">
            <BBALoader size={32} />
          </div>
        ) : tiles.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-10">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${activeStage.tile}`}>
              <Briefcase className={`w-7 h-7 ${activeStage.text}`} />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{activeStage.empty}</p>
          </div>
        ) : (
          <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 xl:grid-cols-3'}`}>
            {tiles.slice(0, 9).map((tile) => (
              <button
                key={tile.id}
                type="button"
                onClick={tile.onClick}
                className="group flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-left transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <ContactWithLogo account={tile.account} company={tile.company} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{tile.company}</p>
                  {tile.subtitle && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{tile.subtitle}</p>
                  )}
                  {tile.meta && (
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      <span className={`w-1.5 h-1.5 rounded-full ${activeStage.dot}`} />
                      {tile.meta}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors shrink-0" />
              </button>
            ))}
          </div>
        )}
        {!loading && tiles.length > 9 && (
          <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
            +{tiles.length - 9} more in {activeStage.label}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate('/crm/fulfillment/hr')}
        className="mt-5 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      >
        Open Hiring Board
      </button>
    </div>
  );
};

export default HRFulfillmentWidgets;
